import type { Card, Suit } from '@durak/shared';
import { rankIndex, SUIT_COLOR, SUIT_SYMBOL } from '@durak/shared';
import clsx from 'clsx';
import { motion, AnimatePresence, type PanInfo } from 'framer-motion';
import { memo, useMemo, useRef } from 'react';
import { FaceCardArt } from './FaceCardArt';
import { resolveDropTarget, type DropTarget } from '../lib/dropTarget';

const FACE_RANKS = new Set(['J', 'Q', 'K']);

export type { DropTarget } from '../lib/dropTarget';

interface Props {
  cards: Card[];
  selectedId?: string | null;
  onSelect?: (card: Card) => void;
  /** Fired when a card is "thrown" — either swiped up or dropped on a slot/table. */
  onCommit?: (card: Card, dropTarget: DropTarget | null) => void;
  /** Fired when the player starts dragging a card (still in hand). */
  onDragStart?: (card: Card) => void;
  /** Fired when the drag ends (whether or not it became a commit). */
  onDragEnd?: () => void;
  trumpSuit?: Suit | null;
  disabledIds?: Set<string>;
  /** Map of slot index → slot element, measured live at drop time. */
  slotRects?: Map<number, HTMLElement>;
  /** Table container element, used to detect a "drop next to a slot" → transfer. */
  tableRectRef?: { current: HTMLElement | null };
}

export function Hand({ cards, selectedId, onSelect, onCommit, onDragStart, onDragEnd, trumpSuit, disabledIds, slotRects, tableRectRef }: Props) {
  // Memoize the sort: a fresh array on every render would invalidate
  // referential equality and force every HandCard to re-render even when
  // nothing changed. We only re-sort when the hand contents or trump change.
  const sorted = useMemo(() => {
    const arr = [...cards];
    arr.sort((a, b) => {
      // Rank order, not string order — lexically '10' sorts before '2' and
      // 'Q' after 'K'. The 52-rank table covers both deck sizes.
      if (a.suit === b.suit) return rankIndex(a.rank, 52) - rankIndex(b.rank, 52);
      if (a.suit === trumpSuit) return 1;
      if (b.suit === trumpSuit) return -1;
      return a.suit.localeCompare(b.suit);
    });
    return arr;
    // Re-sort only when the actual set of cards changes (string key) or trump.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cards.map((c) => c.id).join('|'), trumpSuit]);
  // When the hand grows past 6 cards, increase the overlap so the row never
  // bleeds off the screen. The shift compounds with the existing -20/-22 px
  // base overlap declared in CSS — each card past 6 adds 5 px of overlap.
  const extraOverlap = Math.max(0, sorted.length - 6) * 5;
  return (
    <div
      className="hand-fan"
      style={extraOverlap ? ({ '--extra-overlap': `-${extraOverlap}px` } as React.CSSProperties) : undefined}
    >
      <AnimatePresence initial={false}>
        {sorted.map((c) => (
          <HandCard
            key={c.id}
            card={c}
            selected={selectedId === c.id}
            disabled={disabledIds?.has(c.id)}
            trumpSuit={trumpSuit}
            onSelect={onSelect}
            onCommit={onCommit}
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
            slotRects={slotRects}
            tableRectRef={tableRectRef}
          />
        ))}
      </AnimatePresence>
    </div>
  );
}

interface HandCardProps {
  card: Card;
  selected: boolean;
  disabled?: boolean;
  trumpSuit?: Suit | null;
  onSelect?: (card: Card) => void;
  onCommit?: (card: Card, dropTarget: DropTarget | null) => void;
  onDragStart?: (card: Card) => void;
  onDragEnd?: () => void;
  slotRects?: Map<number, HTMLElement>;
  tableRectRef?: { current: HTMLElement | null };
}

const HandCard = memo(function HandCard({ card, selected, disabled, trumpSuit, onSelect, onCommit, onDragStart, onDragEnd, slotRects, tableRectRef }: HandCardProps) {
  const isTrump = card.suit === trumpSuit;
  const color = SUIT_COLOR[card.suit];
  const sym = SUIT_SYMBOL[card.suit];
  const elRef = useRef<HTMLDivElement>(null);

  function handleDragEnd(_e: any, info: PanInfo) {
    onDragEnd?.();
    if (disabled) return;
    // Players aim with the card they're dragging, not the pointer — the grab
    // point can sit a full card-height below the card's top edge, so hit-test
    // the card's own rect too (resolveDropTarget). All rects are measured now,
    // not cached: the flex-wrapped table reflows every time a card is added.
    // info.point is in page coordinates; getBoundingClientRect is viewport-relative.
    const pointer = { x: info.point.x - window.scrollX, y: info.point.y - window.scrollY };
    const cardRect = elRef.current?.getBoundingClientRect() ?? null;
    const slots: Array<[number, DOMRect]> = slotRects
      ? Array.from(slotRects.entries(), ([idx, el]) => [idx, el.getBoundingClientRect()])
      : [];
    const target = resolveDropTarget(
      pointer,
      cardRect,
      slots,
      tableRectRef?.current?.getBoundingClientRect() ?? null,
    );
    // No drop target: a sufficient swipe-up still commits.
    const liftedEnough = info.offset.y < -90 || info.velocity.y < -500;
    if (target || liftedEnough) {
      onCommit?.(card, target);
    }
  }
  function handleDragStart() {
    if (disabled) return;
    onDragStart?.(card);
  }

  return (
    <motion.div
      ref={elRef}
      layoutId={`card-${card.id}`}
      drag={disabled ? false : true}
      dragSnapToOrigin
      dragElastic={0.5}
      dragMomentum={false}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      whileDrag={{ scale: 1.08, zIndex: 9999, cursor: 'grabbing' }}
      onTap={() => !disabled && onSelect?.(card)}
      whileTap={!disabled ? { scale: 0.97 } : undefined}
      initial={{ opacity: 0, y: -90, scale: 0.78, rotate: -6 }}
      animate={{ opacity: 1, y: selected ? -22 : 0, scale: 1, rotate: 0, zIndex: selected ? 10 : 1 }}
      exit={{ opacity: 0, y: -28, scale: 0.92, transition: { duration: 0.18, ease: 'easeOut' } }}
      transition={{
        default: { type: 'spring', stiffness: 220, damping: 24, mass: 0.9 },
        y: { type: 'spring', stiffness: 320, damping: 26 },
        opacity: { type: 'tween', duration: 0.28, ease: 'easeOut' },
      }}
      className={clsx(
        'playing-card relative',
        color === 'red' && 'red',
        !disabled && 'selectable',
        selected && 'selected',
        disabled && 'disabled',
        isTrump && 'trump-card',
      )}
      style={{ touchAction: 'none' }}
      role="button"
      aria-label={`${card.rank} of ${card.suit}`}
    >
      <span className="playing-card-corner playing-card-corner-tl">
        <span className="playing-card-rank" data-rank={card.rank}>{card.rank}</span>
        <span className="playing-card-suit">{sym}</span>
      </span>
      {FACE_RANKS.has(card.rank) ? (
        <span className="playing-card-face">
          <FaceCardArt rank={card.rank as 'J' | 'Q' | 'K'} suit={card.suit} />
        </span>
      ) : (
        <span className="playing-card-center">{sym}</span>
      )}
      <span className="playing-card-corner playing-card-corner-br">
        <span className="playing-card-rank" data-rank={card.rank}>{card.rank}</span>
        <span className="playing-card-suit">{sym}</span>
      </span>
    </motion.div>
  );
});
